const setTheme = (theme) => {
  if (theme !== 'dark') {
    theme = 'light';
  }

  document.documentElement.setAttribute('data-theme', theme);

  try {
    localStorage.setItem('lostFoundTheme', theme);
  } catch (error) {
    // Theme choice is cosmetic, so ignore storage failures.
  }

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute('content', theme === 'dark' ? '#080807' : '#fbfbf8');
  }

  document.querySelectorAll('[data-theme-select]').forEach((select) => {
    select.value = theme;
  });
};

const getActiveTheme = () => document.documentElement.getAttribute('data-theme') || 'light';

const initRevealAnimations = () => {
  // NOTE: above-the-fold LCP elements (.product__media-main, .product__info,
  // .page-header) are deliberately excluded — reveal hides them with opacity:0
  // until JS runs, which would delay Largest Contentful Paint.
  const revealTargets = document.querySelectorAll([
    '.section',
    '.collection-hero',
    '.product-card',
    '.collection-card',
    '.brand-card',
    '.footer'
  ].join(','));

  revealTargets.forEach((target, index) => {
    target.classList.add('reveal');
    target.style.setProperty('--reveal-delay', `${Math.min(index % 8, 7) * 45}ms`);
  });

  const reveal = (target) => target.classList.add('is-visible');

  if (!('IntersectionObserver' in window)) {
    revealTargets.forEach(reveal);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      reveal(entry.target);
      observer.unobserve(entry.target);
    });
    // threshold 0 so very tall sections (e.g. large collections on mobile) still reveal
    // as soon as any part enters the viewport, instead of waiting for an 8% ratio.
  }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });

  revealTargets.forEach((target) => observer.observe(target));

  // Fail-safe: never leave content permanently hidden if the observer doesn't fire.
  setTimeout(() => revealTargets.forEach(reveal), 1600);
};

const initAmbientMotion = () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(() => {
      document.body.style.setProperty('--ambient-shift', `${Math.round(window.scrollY * -0.018)}px`);
      ticking = false;
    });
  }, { passive: true });
};

const initCountryFilters = () => {
  document.querySelectorAll('[data-country-filter]').forEach((input) => {
    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      const panel = input.closest('.loc-selector__panel');
      panel?.querySelectorAll('[data-country-option]').forEach((option) => {
        const name = option.getAttribute('data-country-name')?.toLowerCase() || '';
        option.hidden = query !== '' && !name.includes(query);
      });
    });
  });
};

// --- Product gallery: sync the active thumbnail with the scrolled slide ---
// (The old idle-preload of 1200x1500 variants was removed: after the carousel
// rewrite nothing ever displayed those URLs — pure wasted bandwidth.)
const initProductGallery = () => {
  const thumbs = document.querySelectorAll('[data-product-thumb]');
  if (thumbs.length < 2) return;
  const pcar = document.querySelector('[data-product-carousel]');
  if (pcar) {
    const counter = document.querySelector('[data-carousel-counter]');
    const total = pcar.querySelectorAll('.product__slide').length;
    let praf = 0;
    pcar.addEventListener('scroll', () => {
      if (praf) return;
      praf = requestAnimationFrame(() => {
        praf = 0;
        const pidx = Math.round(pcar.scrollLeft / (pcar.clientWidth || 1));
        thumbs.forEach((tb, i) => { tb.classList.toggle('is-active', i === pidx); tb.setAttribute('aria-current', i === pidx ? 'true' : 'false'); });
        if (counter) counter.textContent = (Math.min(pidx, total - 1) + 1) + ' / ' + total;
      });
    }, { passive: true });
  }
};

// --- Featured section "random" mode: shuffle the rendered pool down to N cards ---
const initFeaturedShuffle = () => {
  document.querySelectorAll('[data-shuffle]').forEach((grid) => {
    const keep = parseInt(grid.getAttribute('data-shuffle'), 10) || 4;
    const cards = Array.from(grid.children);
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    cards.forEach((card, i) => {
      if (i < keep) grid.appendChild(card);
      else card.remove();
    });
  });
};

// --- Featured section "recommendations" mode: pull Shopify's related products ---
const initRecommendations = () => {
  document.querySelectorAll('[data-recommendations][data-url]').forEach((grid) => {
    fetch(grid.getAttribute('data-url'))
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recommendations]');
        // Only swap when Shopify actually returned recommendations; otherwise keep the
        // collection fallback that's already on screen.
        if (fresh && fresh.hasAttribute('data-recs-ready') && fresh.querySelector('.product-card')) {
          grid.innerHTML = fresh.innerHTML;
          // Re-bind what DOMContentLoaded already did for the replaced cards
          fadeInImages(grid);
          initCardGalleries(grid);
        }
      })
      .catch(() => {});
  });
};

// --- Lazy image fade-in: reveal once loaded (re-runnable for dynamically added cards) ---
const fadeInImages = (root) => {
  (root || document).querySelectorAll('.card-gallery__img, .product__thumbs img').forEach((img) => {
    if (img.dataset.fadeBound) return;
    img.dataset.fadeBound = '1';
    const done = () => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth > 0) done();
    else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
  });
};

const initCardGalleries = (root) => {
  (root || document).querySelectorAll('[data-card-gallery]').forEach((g) => {
    if (g.dataset.galleryBound) return;
    g.dataset.galleryBound = '1';
    const track = g.querySelector('.card-gallery__track');
    if (!track) return;
    const slides = track.querySelectorAll('.card-gallery__slide');
    if (slides.length < 2) return;
    const dots = g.querySelectorAll('.card-gallery__dot');
    const imgs = track.querySelectorAll('.card-gallery__img');
    const prev = g.querySelector('.card-gallery__nav--prev');
    const next = g.querySelector('.card-gallery__nav--next');
    const last = slides.length - 1;
    let raf = 0;
    const load = (i) => { const im = imgs[i]; if (im && im.dataset.src) { im.src = im.dataset.src; im.removeAttribute('data-src'); } };
    const sync = () => {
      raf = 0;
      const w = track.clientWidth || 1;
      const idx = Math.round(track.scrollLeft / w);
      load(idx); load(idx + 1); load(idx - 1);
      dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      if (prev) prev.hidden = idx <= 0;
      if (next) next.hidden = idx >= last;
      // Touch: hand the spotlight to the card whose gallery is being swiped
      if (window.matchMedia('(hover: none)').matches) {
        const card = g.closest('.product-card');
        if (card) document.dispatchEvent(new CustomEvent('lf:gallery-interact', { detail: card }));
      }
    };
    track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true });
    track.addEventListener('pointerenter', () => load(1), { once: true });
    const go = (dir) => (e) => {
      e.preventDefault(); e.stopPropagation();
      const w = track.clientWidth || 1;
      load(Math.round(track.scrollLeft / w) + dir);
      track.scrollBy({ left: dir * w, behavior: 'smooth' });
    };
    if (prev) prev.addEventListener('click', go(-1));
    if (next) next.addEventListener('click', go(1));
  });
};

const initProductZoom = () => {
  const carousel = document.querySelector('[data-product-carousel]');
  if (!carousel) return;
  const slides = Array.from(carousel.querySelectorAll('.product__slide'));
  if (!slides.length) return;
  const open = (startIdx) => {
    const overlay = document.createElement('div');
    overlay.className = 'pdp-zoom';
    const track = document.createElement('div');
    track.className = 'pdp-zoom__track';
    slides.forEach((s) => {
      const fig = document.createElement('div');
      fig.className = 'pdp-zoom__slide';
      const img = document.createElement('img');
      const im = s.querySelector('img');
      img.src = s.getAttribute('data-zoom-src') || (im && (im.currentSrc || im.src)) || '';
      img.alt = (im && im.alt) || '';
      fig.appendChild(img);
      track.appendChild(fig);
    });
    const closeBtn = document.createElement('button');
    closeBtn.className = 'pdp-zoom__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    // Pinch zoom (2 fingers) + one-finger pan while zoomed; releasing near 1x snaps back.
    track.querySelectorAll('.pdp-zoom__slide').forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      let scale = 1, tx = 0, ty = 0;
      let startDist = 0, startScale = 1, startTx = 0, startTy = 0, startMidX = 0, startMidY = 0;
      let panning = false, panStartX = 0, panStartY = 0;
      const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
      const apply = (animate) => {
        img.style.transition = animate ? 'transform .25s ease' : 'none';
        img.style.transform = scale > 1.001 ? 'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px) scale(' + scale.toFixed(3) + ')' : '';
        const zoomed = scale > 1.02;
        track.style.overflowX = zoomed ? 'hidden' : '';
        track.style.scrollSnapType = zoomed ? 'none' : '';
      };
      const clampPan = () => {
        const maxX = ((scale - 1) * img.offsetWidth) / 2;
        const maxY = ((scale - 1) * img.offsetHeight) / 2;
        tx = Math.max(-maxX, Math.min(maxX, tx));
        ty = Math.max(-maxY, Math.min(maxY, ty));
      };
      slide.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          startDist = dist(e.touches);
          startScale = scale;
          startTx = tx;
          startTy = ty;
          startMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          startMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        } else if (e.touches.length === 1 && scale > 1.02) {
          panning = true;
          panStartX = e.touches[0].clientX - tx;
          panStartY = e.touches[0].clientY - ty;
        }
      }, { passive: false });
      slide.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2 && startDist) {
          e.preventDefault();
          const newScale = Math.max(1, Math.min(4, startScale * (dist(e.touches) / startDist)));
          const r = slide.getBoundingClientRect();
          const k = newScale / (startScale || 1);
          // keep the pinch midpoint anchored while scaling
          tx = startTx * k + (1 - k) * (startMidX - (r.left + r.width / 2));
          ty = startTy * k + (1 - k) * (startMidY - (r.top + r.height / 2));
          scale = newScale;
          clampPan();
          apply(false);
        } else if (panning && e.touches.length === 1 && scale > 1.02) {
          e.preventDefault();
          tx = e.touches[0].clientX - panStartX;
          ty = e.touches[0].clientY - panStartY;
          clampPan();
          apply(false);
        }
      }, { passive: false });
      slide.addEventListener('touchend', (e) => {
        if (e.touches.length < 2) startDist = 0;
        if (e.touches.length === 0) {
          panning = false;
          if (scale < 1.06) { scale = 1; tx = 0; ty = 0; apply(true); }
        }
      });
    });
    overlay.appendChild(track);
    overlay.appendChild(closeBtn);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => { track.scrollLeft = startIdx * track.clientWidth; });
    let closed = false;
    let popped = false;
    const remove = () => {
      if (closed) return;
      closed = true;
      overlay.remove();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
    };
    const onPop = () => remove(); // hardware / browser Back closes the lightbox instead of leaving the page
    const destroy = () => { remove(); if (!popped && history.state && history.state.lfZoom) { popped = true; history.back(); } };
    const onKey = (e) => { if (e.key === 'Escape') destroy(); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);
    try { history.pushState({ lfZoom: 1 }, ''); } catch (e) {}
    // Click anywhere that isn't the photo itself closes (backdrop, letterboxing, padding, close button).
    overlay.addEventListener('click', (e) => { if (e.target.tagName !== 'IMG') destroy(); });
  };
  slides.forEach((s, i) => {
    const im = s.querySelector('img');
    if (im) { im.style.cursor = 'zoom-in'; im.addEventListener('click', () => open(i)); }
  });
};

const initPdpSticky = () => {
  const bar = document.querySelector('[data-pdp-sticky]');
  if (!bar) return;
  document.body.appendChild(bar); // .main のスタッキングコンテキストから出す（フッターが上に描画されるため）
  const mainBtn = document.querySelector('.product-form button[name="add"]');
  const stickyAdd = bar.querySelector('[data-pdp-sticky-add]');
  if (stickyAdd && mainBtn) stickyAdd.addEventListener('click', () => mainBtn.click());
  const anchor = mainBtn || document.querySelector('.product-form');
  if (!anchor || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => { entries.forEach((e) => { bar.hidden = e.isIntersecting; }); }, { rootMargin: '0px 0px -60px 0px' });
  io.observe(anchor);
};

// --- Autoplay muted videos reliably (the autoplay attribute can be deferred by the
//     browser with preload=metadata); play in view, pause offscreen to save battery ---
const initAutoplayVideos = () => {
  const vids = document.querySelectorAll('video[autoplay][muted]');
  if (!vids.length) return;
  const tryPlay = (v) => { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); };
  if (!('IntersectionObserver' in window)) { vids.forEach(tryPlay); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) tryPlay(e.target);
      else if (!e.target.paused) e.target.pause();
    });
  }, { rootMargin: '100px 0px' });
  vids.forEach((v) => io.observe(v));
};

// --- 3D tilt + cursor-follow glare on hero product media (pointer devices only) ---
const initTiltMedia = () => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('[data-tilt]').forEach((wrap) => {
    let glare = wrap.querySelector('.tilt-glare');
    if (!glare) {
      glare = document.createElement('span');
      glare.className = 'tilt-glare';
      glare.setAttribute('aria-hidden', 'true');
      wrap.appendChild(glare);
    }
    let raf = 0;
    wrap.addEventListener('pointermove', (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = wrap.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const px = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
        const py = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
        wrap.style.setProperty('--tilt-rx', ((0.5 - py) * 7).toFixed(2) + 'deg');
        wrap.style.setProperty('--tilt-ry', ((px - 0.5) * 9).toFixed(2) + 'deg');
        wrap.style.setProperty('--glare-x', (px * 100).toFixed(1) + '%');
        wrap.style.setProperty('--glare-y', (py * 100).toFixed(1) + '%');
        wrap.classList.add('is-tilting');
      });
    });
    wrap.addEventListener('pointerleave', () => {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      wrap.classList.remove('is-tilting');
      wrap.style.setProperty('--tilt-rx', '0deg');
      wrap.style.setProperty('--tilt-ry', '0deg');
    });
  });
};

// --- Mobile: gyroscope-driven tilt/glare for [data-tilt] (skipped on iOS, which
//     requires a permission prompt; desktop uses the pointer version instead) ---
const initGyroTilt = () => {
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('DeviceOrientationEvent' in window)) return;
  if (typeof DeviceOrientationEvent.requestPermission === 'function') return;
  const targets = Array.from(document.querySelectorAll('[data-tilt]'));
  if (!targets.length) return;
  targets.forEach((wrap) => {
    if (!wrap.querySelector('.tilt-glare')) {
      const glare = document.createElement('span');
      glare.className = 'tilt-glare';
      glare.setAttribute('aria-hidden', 'true');
      wrap.appendChild(glare);
    }
  });
  let base = null;
  let raf = 0;
  let lastB = 0;
  let lastG = 0;
  window.addEventListener('deviceorientation', (e) => {
    if (e.beta == null || e.gamma == null) return;
    if (base === null) base = { b: e.beta, g: e.gamma };
    lastB = e.beta;
    lastG = e.gamma;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      // slowly re-center so holding the phone at a new angle becomes the neutral pose
      base.b += (lastB - base.b) * 0.01;
      base.g += (lastG - base.g) * 0.01;
      const db = Math.max(-18, Math.min(18, lastB - base.b));
      const dg = Math.max(-18, Math.min(18, lastG - base.g));
      const rx = ((-db / 18) * 5).toFixed(2) + 'deg';
      const ry = ((dg / 18) * 6).toFixed(2) + 'deg';
      const gx = (50 + (dg / 18) * 36).toFixed(1) + '%';
      const gy = (50 + (db / 18) * 36).toFixed(1) + '%';
      targets.forEach((wrap) => {
        wrap.style.setProperty('--tilt-rx', rx);
        wrap.style.setProperty('--tilt-ry', ry);
        wrap.style.setProperty('--glare-x', gx);
        wrap.style.setProperty('--glare-y', gy);
        wrap.classList.add('is-tilting');
      });
    });
  }, true);
};

// --- Variant picker: option pills drive price / stock / ATC / media / URL ---
const initVariantPicker = () => {
  const picker = document.querySelector('[data-variant-picker]');
  if (!picker) return;
  let variants;
  try { variants = JSON.parse(picker.querySelector('[data-vp-data]').textContent); } catch (e) { return; }
  if (!Array.isArray(variants) || !variants.length) return;
  const groups = Array.from(picker.querySelectorAll('.variant-picker__group'));
  const idInput = document.querySelector('[data-vp-id]');
  const priceWrap = document.querySelector('.product__info .price');
  const atc = document.querySelector('.product-form button[name="add"]');
  const restockEl = document.querySelector('[data-restock]');
  const sticky = document.querySelector('[data-pdp-sticky]');
  const stockBar = document.querySelector('[data-stock-bar]');
  const esc = (x) => String(x).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const selected = groups.map((g) => {
    const on = g.querySelector('.variant-picker__pill.is-selected');
    return on ? on.getAttribute('data-vp-value') : null;
  });
  const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));
  const render = () => {
    groups.forEach((g, gi) => {
      g.querySelectorAll('.variant-picker__pill').forEach((pill) => {
        const val = pill.getAttribute('data-vp-value');
        const on = selected[gi] === val;
        pill.classList.toggle('is-selected', on);
        pill.setAttribute('aria-pressed', on ? 'true' : 'false');
        const test = selected.slice();
        test[gi] = val;
        const tv = findVariant(test);
        pill.classList.toggle('is-oos', !(tv && tv.available));
      });
      const cur = g.querySelector('[data-vp-current]');
      if (cur) cur.textContent = selected[gi] || '';
    });
    const v = findVariant(selected);
    if (!v) return;
    if (idInput) idInput.value = v.id;
    if (priceWrap) {
      priceWrap.innerHTML = v.compare
        ? '<span class="price__sale">' + esc(v.price) + '</span> <s class="price__compare">' + esc(v.compare) + '</s>'
        : '<span>' + esc(v.price) + '</span>';
    }
    const addLabel = atc ? atc.getAttribute('data-label-add') : '';
    const soldLabel = atc ? atc.getAttribute('data-label-soldout') : '';
    if (atc) { atc.disabled = !v.available; atc.textContent = v.available ? addLabel : soldLabel; }
    if (sticky) {
      const sp = sticky.querySelector('.pdp-sticky__price');
      if (sp) sp.textContent = v.price;
      const sb = sticky.querySelector('.pdp-sticky__btn');
      if (sb) { sb.disabled = !v.available; if (addLabel) sb.textContent = v.available ? addLabel : soldLabel; }
    }
    if (stockBar) {
      const thr = parseInt(stockBar.getAttribute('data-threshold'), 10) || 10;
      // data-always: 残数が閾値を超えていても常に表示（バーの基準は data-full）
      const always = stockBar.hasAttribute('data-always');
      const full = parseInt(stockBar.getAttribute('data-full'), 10) || thr;
      const show = v.qty != null && v.available && v.qty > 0 && (always || v.qty <= thr);
      stockBar.hidden = !show;
      if (show) {
        const base = always ? full : thr;
        const fill = stockBar.querySelector('.stock-bar__fill');
        if (fill) fill.style.width = Math.min(100, Math.round((v.qty / base) * 100)) + '%';
        stockBar.classList.toggle('stock-bar--low', v.qty <= thr);
        const lbl = stockBar.querySelector('.stock-bar__label');
        const tpl = (always && v.qty > thr && stockBar.getAttribute('data-tpl-many')) || stockBar.getAttribute('data-tpl') || '';
        if (lbl && tpl) lbl.innerHTML = tpl.replace('__N__', v.qty);
      }
    }
    if (restockEl) {
      restockEl.hidden = !!v.available;
      const rb = restockEl.querySelector('[data-restock-body]');
      if (rb) {
        const base = rb.getAttribute('data-base') || rb.value;
        rb.value = base + (v.options && v.options.length ? ' [' + v.options.join(' / ') + ']' : '');
      }
    }
    if (v.media != null) {
      const car = document.querySelector('[data-product-carousel]');
      if (car) car.scrollTo({ left: v.media * (car.clientWidth || 1), behavior: 'smooth' });
    }
    try {
      const u = new URL(location.href);
      u.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', u);
    } catch (e) {}
  };
  picker.addEventListener('click', (e) => {
    const pill = e.target.closest('.variant-picker__pill');
    if (!pill) return;
    selected[Number(pill.getAttribute('data-vp-option'))] = pill.getAttribute('data-vp-value');
    render();
  });
  render();
};

// --- Touch: spotlight the product card nearest the viewport center (mobile "hover") ---
const initCenterFocus = () => {
  if (!window.matchMedia('(hover: none)').matches) return;
  let cards = Array.from(document.querySelectorAll('.product-card'));
  if (!cards.length) return;
  const visible = new Set();
  let current = null;
  let holdUntil = 0; // while set, a swiped card keeps the spotlight (gallery interaction wins over center logic)
  const update = () => {
    if (Date.now() < holdUntil) return;
    const all = document.querySelectorAll('.product-card');
    if (all.length !== cards.length || !cards.every((c, i) => c === all[i])) { cards = Array.from(all); observeAll(); }
    const mid = window.innerHeight / 2;
    let best = null;
    let bestDist = Infinity;
    visible.forEach((c) => {
      const r = c.getBoundingClientRect();
      const d = Math.abs(r.top + r.height / 2 - mid);
      if (d < bestDist) { bestDist = d; best = c; }
    });
    if (best && bestDist > window.innerHeight * 0.32) best = null;
    if (best !== current) {
      if (current) current.classList.remove('is-focused');
      current = best;
      if (current) current.classList.add('is-focused');
    }
  };
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) visible.add(e.target);
      else {
        visible.delete(e.target);
        e.target.classList.remove('is-focused');
        if (e.target === current) { current = null; holdUntil = 0; } // swiped card scrolled away — release the hold
      }
    });
    update();
  }, { rootMargin: '10% 0px' });
  // Swiping a card's image gallery moves the spotlight to that card and holds it there briefly
  document.addEventListener('lf:gallery-interact', (e) => {
    const card = e.detail;
    if (!card || !document.body.contains(card)) return;
    holdUntil = Date.now() + 2500;
    if (card !== current) {
      if (current) current.classList.remove('is-focused');
      current = card;
      card.classList.add('is-focused');
    }
  });
  const observeAll = () => cards.forEach((c) => { if (!c.dataset.focusBound) { c.dataset.focusBound = '1'; io.observe(c); } });
  observeAll();
  let raf = 0;
  window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); }); }, { passive: true });
  update();
};

// --- Add-to-cart: fly a small copy of the product image into the cart icon ---
const flyToCart = (form) => {
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    const cartIcon = document.querySelector('.header__cart');
    if (!cartIcon) return false;
    const card = form.closest('.product-card');
    const srcEl = card
      ? card.querySelector('.product-card__media img')
      : document.querySelector('.product__media-main img, .product__media-main video, .rp__slide.is-active img, .msp__pack-image');
    if (!srcEl) return false;
    const src = srcEl.tagName === 'VIDEO' ? srcEl.poster : (srcEl.currentSrc || srcEl.src);
    if (!src) return false;
    const from = srcEl.getBoundingClientRect();
    const to = cartIcon.getBoundingClientRect();
    if (!from.width || !to.width) return false;
    const size = 64;
    const ghost = document.createElement('div');
    ghost.className = 'fly-to-cart';
    ghost.style.backgroundImage = "url('" + src + "')";
    ghost.style.left = (from.left + from.width / 2 - size / 2) + 'px';
    ghost.style.top = (from.top + from.height / 2 - size / 2) + 'px';
    document.body.appendChild(ghost);
    if (!ghost.animate) { ghost.remove(); return false; }
    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    ghost.animate([
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: 'translate(' + dx * 0.55 + 'px, ' + (dy * 0.55 - 80) + 'px) scale(.6)', opacity: .95, offset: .6 },
      { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(.16)', opacity: .25 }
    ], { duration: 620, easing: 'cubic-bezier(.3, .6, .4, 1)' }).onfinish = () => {
      ghost.remove();
      cartIcon.classList.add('cart-pop');
      setTimeout(() => cartIcon.classList.remove('cart-pop'), 500);
    };
    setTimeout(() => ghost.remove(), 1200); // hard cleanup even if onfinish is throttled
    return true;
  } catch (e) { return false; }
};

// --- Wishlist heart: tiny particle burst when saving ---
const burstHearts = (btn) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const r = btn.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  for (let i = 0; i < 6; i++) {
    const pt = document.createElement('span');
    pt.className = 'heart-burst';
    pt.textContent = '\u2665';
    pt.style.left = cx + 'px';
    pt.style.top = cy + 'px';
    document.body.appendChild(pt);
    if (!pt.animate) { pt.remove(); return; }
    const ang = (Math.PI * 2 * i) / 6 + Math.random() * 0.7;
    const d = 26 + Math.random() * 20;
    pt.animate([
      { transform: 'translate(-50%, -50%) scale(.5)', opacity: 1 },
      { transform: 'translate(calc(-50% + ' + (Math.cos(ang) * d).toFixed(1) + 'px), calc(-50% + ' + (Math.sin(ang) * d - 14).toFixed(1) + 'px)) scale(1)', opacity: 0 }
    ], { duration: 620 + Math.random() * 180, easing: 'cubic-bezier(.2, .6, .3, 1)' }).onfinish = () => pt.remove();
    setTimeout(() => pt.remove(), 1400); // hard cleanup even if onfinish is throttled
  }
};

// --- Country selector (<details>): close on outside click / Escape ---
const initLocalizationClose = () => {
  const items = document.querySelectorAll('.loc-selector');
  if (!items.length) return;
  document.addEventListener('click', (e) => {
    items.forEach((d) => { if (d.open && !d.contains(e.target)) d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') items.forEach((d) => { if (d.open) d.removeAttribute('open'); });
  });
};

document.addEventListener('DOMContentLoaded', () => {
  setTheme(getActiveTheme());
  initRevealAnimations();
  initAmbientMotion();
  initCountryFilters();
  initProductGallery();
  initFeaturedShuffle();
  initRecommendations();
  initCartDrawer();
  initWishlist();
  initDropCountdown();
  initAnnouncement();
  initCardGalleries();
  initProductZoom();
  initPdpSticky();
  initLocalizationClose();
  initAutoplayVideos();
  fadeInImages();
  initRecentlyViewed();
  initPredictiveSearch();
  initTiltMedia();
  initGyroTilt();
  initCenterFocus();
  initVariantPicker();
  initSignupPopup();
  initPreorderRotator();
  initNewsManifest();
  initSnsEmbeds();
  initSnsCarousels();
});

// --- プレオーダー特集: 複数商品のクロスフェード自動切替 ---
const initPreorderRotator = () => {
  // 締切を過ぎたスライドはキャッシュ済みHTMLでも取り除く（サーバー側でも除外している）
  document.querySelectorAll('[data-preorder-rotator]').forEach((stack) => {
    stack.querySelectorAll('.preorder__slide[data-po-deadline]').forEach((slide) => {
      const raw = (slide.getAttribute('data-po-deadline') || '').trim();
      let iso = raw.replace(/\//g, '-').replace(' ', 'T');
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) iso += ':00';
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(iso)) iso += '+09:00';
      const t = new Date(iso);
      if (!isNaN(t.getTime()) && Date.now() >= t.getTime()) slide.remove();
    });
    if (!stack.querySelector('.preorder__slide')) {
      const sec = stack.closest('.preorder');
      if (sec) sec.remove();
    } else if (!stack.querySelector('.preorder__slide.is-active')) {
      stack.querySelector('.preorder__slide').classList.add('is-active');
    }
  });
  document.querySelectorAll('[data-preorder-rotator]').forEach((stack) => {
    const slides = Array.from(stack.querySelectorAll('.preorder__slide'));
    if (slides.length < 2) return;
    const section = stack.closest('.preorder') || stack;
    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'preorder__dots';
    let idx = 0, timer = 0;
    const INTERVAL = (parseFloat(stack.getAttribute('data-interval')) || 7) * 1000;
    const go = (n) => {
      idx = (n + slides.length) % slides.length;
      slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === idx);
        // 非表示スライドの動画は止める（バッテリー/CPU節約）
        s.querySelectorAll('video').forEach((v) => {
          if (i === idx) { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); }
          else v.pause();
        });
      });
      dotsWrap.querySelectorAll('.preorder__dot').forEach((d, i) => d.classList.toggle('is-active', i === idx));
    };
    slides.forEach((s, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'preorder__dot' + (i === 0 ? ' is-active' : '');
      d.setAttribute('aria-label', (i + 1) + ' / ' + slides.length);
      d.addEventListener('click', () => { go(i); d.blur(); restart(); });
      dotsWrap.appendChild(d);
    });
    stack.parentElement.appendChild(dotsWrap);
    const stop = () => { if (timer) { clearInterval(timer); timer = 0; } };
    const start = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stop();
      timer = setInterval(() => { if (!document.hidden) go(idx + 1); }, INTERVAL);
    };
    const restart = () => { stop(); start(); };
    // ホバー停止は付けない: セクションは画面幅の帯なので、カーソルが乗っているだけで
    // 止まりっぱなしになる（Chromeを見ている間ずっと切り替わらない）。
    // キーボードフォーカス中のみ停止（フェード中にフォーカスが消えるのを防ぐ）。
    section.addEventListener('focusin', stop);
    section.addEventListener('focusout', start);
    start();
  });
};

// --- 入荷情報B案: 台帳リストの固定サイドプレビュー（ホバーでクロスフェード切替） ---
const initNewsManifest = () => {
  const wrap = document.querySelector('[data-news-manifest]');
  const img = document.querySelector('[data-side-img]');
  if (!wrap || !img) return;
  const vEl = document.querySelector('[data-side-vendor]');
  const tEl = document.querySelector('[data-side-title]');
  let current = '';
  const show = (row) => {
    const src = row.getAttribute('data-preview');
    if (vEl) vEl.textContent = row.getAttribute('data-vendor') || '';
    if (tEl) tEl.textContent = row.getAttribute('data-title') || '';
    if (!src || src === current) return;
    current = src;
    img.classList.remove('is-in');
    const pre = new Image();
    pre.onload = () => {
      if (current !== src) return; // 既に別の行へ移っていたら破棄
      img.src = src;
      requestAnimationFrame(() => requestAnimationFrame(() => img.classList.add('is-in')));
    };
    pre.src = src;
  };
  wrap.addEventListener('mouseover', (e) => {
    const row = e.target.closest('.manifest__row');
    if (row) show(row);
  });
  // 初期表示は最新の1件目
  const first = wrap.querySelector('.manifest__row');
  if (first) show(first);
};

// --- SNSページ: 埋め込みスクリプトの遅延読み込み（表示されてから） ---
const initSnsEmbeds = () => {
  const wrap = document.querySelector('[data-sns-embeds]');
  if (!wrap) return;
  const SRC = {
    tiktok: 'https://www.tiktok.com/embed.js',
    x: 'https://platform.twitter.com/widgets.js',
    instagram: 'https://www.instagram.com/embed.js'
  };
  const load = () => {
    const need = new Set([...wrap.querySelectorAll('[data-sns-need]')].map((p) => p.getAttribute('data-sns-need')));
    need.forEach((k) => {
      if (!SRC[k] || document.querySelector('script[src="' + SRC[k] + '"]')) return;
      const s = document.createElement('script');
      s.src = SRC[k]; s.async = true;
      document.body.appendChild(s);
    });
  };
  if (!('IntersectionObserver' in window)) { load(); return; }
  const io = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) { load(); io.disconnect(); }
  }, { rootMargin: '200px 0px' });
  io.observe(wrap);
};

// --- SNSページ: パネル内カルーセル（1件表示・矢印/ドット切替） ---
const initSnsCarousels = () => {
  document.querySelectorAll('[data-sns-carousel]').forEach((car) => {
    const track = car.querySelector('.sns__track');
    const slides = track ? Array.from(track.children) : [];
    if (slides.length < 2) return;
    const nav = document.createElement('div');
    nav.className = 'sns__nav';
    const prev = document.createElement('button');
    prev.type = 'button'; prev.className = 'sns__navbtn'; prev.innerHTML = '&#8592;';
    prev.setAttribute('aria-label', 'Previous');
    const next = document.createElement('button');
    next.type = 'button'; next.className = 'sns__navbtn'; next.innerHTML = '&#8594;';
    next.setAttribute('aria-label', 'Next');
    const dots = document.createElement('div');
    dots.className = 'sns__dots';
    slides.forEach((s, i) => {
      const d = document.createElement('button');
      d.type = 'button'; d.className = 'sns__dot' + (i === 0 ? ' is-active' : '');
      d.setAttribute('aria-label', (i + 1) + ' / ' + slides.length);
      d.addEventListener('click', () => track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }));
      dots.appendChild(d);
    });
    nav.appendChild(prev); nav.appendChild(dots); nav.appendChild(next);
    car.appendChild(nav);
    const idx = () => Math.round(track.scrollLeft / (track.clientWidth || 1));
    // 投稿ごとに高さが違うので、トラックの高さは表示中のスライドに合わせて追従させる
    const setH = () => {
      const s = slides[idx()];
      if (s && s.offsetHeight) track.style.height = s.offsetHeight + 'px';
    };
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => setH());
      slides.forEach((s) => ro.observe(s));
    }
    const sync = () => {
      const i = idx();
      dots.querySelectorAll('.sns__dot').forEach((d, k) => d.classList.toggle('is-active', k === i));
      prev.disabled = i <= 0;
      next.disabled = i >= slides.length - 1;
      setH();
    };
    let raf = 0;
    track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; sync(); }); }, { passive: true });
    prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));
    sync();
  });
};

// --- Email signup popup (theme-native, no app) ---
function initSignupPopup() {
  const popup = document.querySelector('[data-signup-popup]');
  if (!popup) return;
  const KEY = 'lf:signup-popup';
  let state = {};
  try { state = JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { state = {}; }
  const save = () => { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode */ } };

  const openPopup = () => {
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
  };
  const closePopup = (dismissed) => {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    if (dismissed) { state.dismissedAt = Date.now(); save(); }
  };
  popup.querySelectorAll('[data-signup-close]').forEach((el) => el.addEventListener('click', () => closePopup(true)));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup(true);
  });

  const params = new URLSearchParams(location.search);

  // Came back from a successful popup submit (return_to carries ?popup-signup=success)
  if (params.get('popup-signup') === 'success') {
    state.done = true; save();
    const form = popup.querySelector('form');
    if (form) form.hidden = true;
    const later = popup.querySelector('.signup-popup__later');
    if (later) later.hidden = true;
    const ok = popup.querySelector('[data-signup-success]');
    if (ok) ok.hidden = false;
    openPopup();
    setTimeout(() => closePopup(false), 4500);
    params.delete('popup-signup');
    const qs = params.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
    return;
  }

  // Subscribed via the footer form (Shopify redirects with ?customer_posted=true) — never nag again
  if (params.get('customer_posted') === 'true') { state.done = true; save(); return; }

  // A failed popup submit re-renders the page with errors — reopen so the shopper sees the message
  if (popup.querySelector('[data-signup-error]')) { openPopup(); return; }

  if (state.done) return;
  const freqDays = parseFloat(popup.getAttribute('data-frequency')) || 7;
  if (state.dismissedAt && Date.now() - state.dismissedAt < freqDays * 86400000) return;

  const delayMs = (parseFloat(popup.getAttribute('data-delay')) || 12) * 1000;
  const tryOpen = () => {
    // Don't fight the cart drawer for attention — retry later
    if (document.body.classList.contains('cart-drawer-open')) { setTimeout(tryOpen, 15000); return; }
    openPopup();
  };
  setTimeout(tryOpen, delayMs);
}

document.addEventListener('click', (event) => {
  const themeToggle = event.target.closest('[data-theme-toggle]');
  if (themeToggle) {
    setTheme(getActiveTheme() === 'dark' ? 'light' : 'dark');
    return;
  }

  const thumb = event.target.closest('[data-product-thumb]');
  if (!thumb) return;

  const carousel = document.querySelector('[data-product-carousel]');
  if (carousel) {
    const tlist = Array.from(document.querySelectorAll('[data-product-thumb]'));
    const cidx = tlist.indexOf(thumb);
    carousel.scrollTo({ left: cidx * carousel.clientWidth, behavior: 'smooth' });
  }

  document.querySelectorAll('[data-product-thumb]').forEach((t) => {
    t.classList.toggle('is-active', t === thumb);
    t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
  });
});

// --- Add to cart via fetch, so a failure shows an in-theme notice instead of Shopify's default error page ---
const themeStrings = (window.theme && window.theme.strings) || {};
const themeRoutes = (window.theme && window.theme.routes) || {};

let cartToastTimer;
const showCartToast = (message, type, actionLabel, actionUrl) => {
  const toast = document.querySelector('[data-cart-toast]');
  if (!toast) return;
  const escT = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let html = `<span class="cart-toast__msg">${escT(message)}</span>`;
  if (actionLabel && actionUrl) {
    html += `<a class="cart-toast__action" href="${escT(actionUrl)}">${escT(actionLabel)}</a>`;
  }
  toast.innerHTML = html;
  toast.className = `cart-toast cart-toast--${type || 'info'} is-visible`;
  clearTimeout(cartToastTimer);
  cartToastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4400);
};

const refreshCartCount = async () => {
  try {
    const res = await fetch(`${themeRoutes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const cart = await res.json();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  } catch (error) {
    // Count refresh is cosmetic; ignore failures.
  }
};

const addToCart = async (form) => {
  const button = form.querySelector('[type="submit"]');
  const originalLabel = button ? button.textContent : '';
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = themeStrings.adding || 'Adding…';
  }
  try {
    const res = await fetch(`${themeRoutes.cartAdd || '/cart/add'}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      refreshCartCount();
      const flew = flyToCart(form);
      const openOrToast = () => {
        if (window.__openCartDrawer) { window.__openCartDrawer(); } else {
          showCartToast(themeStrings.added || 'Added to cart', 'success', themeStrings.viewCart, themeRoutes.cart || '/cart');
        }
      };
      if (flew) { setTimeout(openOrToast, 680); } else { openOrToast(); }
    } else {
      showCartToast(data.description || data.message || themeStrings.addError || "Couldn't add to cart", 'error');
    }
  } catch (error) {
    showCartToast(themeStrings.addError || "Couldn't add to cart", 'error');
  } finally {
    if (button) {
      button.removeAttribute('aria-busy');
      // リクエスト中にバリエーションピッカーが売り切れ表示に変えた場合は尊重する
      if (button.textContent === (themeStrings.adding || 'Adding…')) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  }
};

document.addEventListener('submit', (event) => {
  const form = event.target.closest('.product-card__form, .product-form');
  if (!form) return;
  event.preventDefault();
  addToCart(form);
});


const initCartDrawer = () => {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isJa = (document.documentElement.lang || 'ja').toLowerCase().indexOf('ja') === 0;
  const T = isJa
    ? { loading: '読み込み中…', empty: 'カートは空です。', loadError: 'カートを読み込めませんでした。', dec: '数量を減らす', inc: '数量を増やす', remove: '削除', changeFail: '数量を変更できませんでした。', network: '数量を変更できませんでした。通信状況をご確認ください。', stockMax: '在庫の上限に達しているため、これ以上増やせません。', upsellAdd: '追加' }
    : { loading: 'Loading…', empty: 'Your cart is empty.', loadError: "Couldn't load your cart.", dec: 'Decrease quantity', inc: 'Increase quantity', remove: 'Remove', changeFail: "Couldn't update the quantity.", network: "Couldn't update the quantity. Please check your connection.", stockMax: 'No more available — stock limit reached.', upsellAdd: 'Add' };
  const body = drawer.querySelector('[data-cart-body]');
  const foot = drawer.querySelector('[data-cart-foot]');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
  // Amounts from /cart.js are in the shopper's presentment currency (×100).
  let cartCurrency = 'JPY';
  const money = (c) => {
    if (cartCurrency === 'JPY') return '¥' + Math.round(c / 100).toLocaleString('ja-JP');
    try {
      return new Intl.NumberFormat(isJa ? 'ja-JP' : 'en-US', { style: 'currency', currency: cartCurrency }).format(c / 100);
    } catch (e) {
      return (c / 100).toFixed(2) + ' ' + cartCurrency;
    }
  };
  const sized = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '');

  const shipEl = drawer.querySelector('[data-free-shipping]');
  const shipMsg = shipEl && shipEl.querySelector('[data-free-shipping-msg]');
  const shipFill = shipEl && shipEl.querySelector('[data-free-shipping-fill]');
  const freeThreshold = shipEl ? (parseInt(shipEl.getAttribute('data-threshold'), 10) || 0) : 0;
  const updateFreeShipping = (cart) => {
    if (!shipEl || !freeThreshold) return;
    // The threshold is a JPY amount — hide the bar for other presentment currencies (intl orders ship paid, not free).
    if (cartCurrency !== 'JPY') { shipEl.hidden = true; return; }
    if (!cart.items || !cart.items.length) { shipEl.hidden = true; return; }
    const sub = (cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price) / 100;
    shipEl.hidden = false;
    const remaining = freeThreshold - sub;
    const pct = Math.max(0, Math.min(100, (sub / freeThreshold) * 100));
    if (shipFill) shipFill.style.width = pct + '%';
    if (remaining > 0) {
      shipEl.classList.remove('is-complete');
      if (shipMsg) shipMsg.innerHTML = isJa ? ('あと <strong>' + money(remaining * 100) + '</strong> で送料無料<span class="cart-drawer__ship-note">（一部地域を除く）</span>') : ('Add <strong>' + money(remaining * 100) + '</strong> for free shipping<span class="cart-drawer__ship-note"> (some regions excl.)</span>');
    } else {
      shipEl.classList.add('is-complete');
      if (shipMsg) shipMsg.innerHTML = isJa ? '🎉 送料無料を達成しました！' : '🎉 You unlocked free shipping!';
    }
  };

  const open = () => {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    render();
  };
  const close = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
  };

  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // --- Cross-sell: suggest cases/sleeves under the cart items ---
  const upsellWrap = drawer.querySelector('[data-cart-upsell]');
  const upsellItems = drawer.querySelector('[data-cart-upsell-items]');
  let upsellCache = null;
  const renderUpsell = async (cart) => {
    if (!upsellWrap || !upsellItems) return;
    // products.json returns prices in the buyer's presentment currency (matches cart.currency),
    // so money() formats them correctly in any currency — show the cross-sell everywhere.
    if (!cart.items || !cart.items.length) { upsellWrap.hidden = true; return; }
    try {
      if (!upsellCache) {
        const res = await fetch(root + 'collections/acrylic-case/products.json?limit=10', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('upsell fetch failed');
        upsellCache = (await res.json()).products || [];
      }
      const inCart = new Set(cart.items.map((i) => i.handle));
      const picks = upsellCache
        .filter((p) => !inCart.has(p.handle) && p.variants.some((v) => v.available))
        .slice(0, 2);
      if (!picks.length) { upsellWrap.hidden = true; return; }
      upsellItems.innerHTML = picks.map((p) => {
        const v = p.variants.find((x) => x.available);
        const src = p.images && p.images[0] && p.images[0].src ? sized(p.images[0].src, 120) : '';
        return '<div class="cart-upsell__item">'
          + (src ? '<img src="' + escapeHtml(src) + '" alt="" loading="lazy" width="48" height="60">' : '')
          + '<div class="cart-upsell__main">'
          + '<a class="cart-upsell__title" href="' + root + 'products/' + escapeHtml(p.handle) + '">' + escapeHtml(p.title) + '</a>'
          + '<span class="cart-upsell__price">' + money(Math.round(parseFloat(v.price) * 100)) + '</span>'
          + '</div>'
          + '<button type="button" class="cart-upsell__add" data-upsell-add="' + v.id + '">' + T.upsellAdd + '</button>'
          + '</div>';
      }).join('');
      upsellWrap.hidden = false;
    } catch (e) { upsellWrap.hidden = true; }
  };
  drawer.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    btn.disabled = true;
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(btn.getAttribute('data-upsell-add')), quantity: 1 }),
      });
      if (res.ok) { render(); refreshCartCount(); }
    } catch (err) { /* leave cart unchanged */ }
    btn.disabled = false;
  });

  const renderItems = (cart) => {
    if (cart.currency) cartCurrency = cart.currency;
    if (!cart.items || !cart.items.length) {
      body.innerHTML = '<p class="cart-drawer__empty">' + T.empty + '</p>';
      foot.hidden = true;
      if (shipEl) shipEl.hidden = true;
      if (upsellWrap) upsellWrap.hidden = true;
      return;
    }
    body.innerHTML = cart.items.map((item) => {
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<span class="cart-drawer__item-variant">' + escapeHtml(item.variant_title) + '</span>' : '';
      const img = item.image ? '<img src="' + escapeHtml(sized(item.image, 160)) + '" alt="" loading="lazy">' : '';
      return '<div class="cart-drawer__item" data-key="' + escapeHtml(item.key) + '">'
        + '<a href="' + escapeHtml(item.url) + '" class="cart-drawer__item-image">' + img + '</a>'
        + '<div class="cart-drawer__item-main">'
        + '<a href="' + escapeHtml(item.url) + '" class="cart-drawer__item-title">' + escapeHtml(item.product_title) + '</a>'
        + variant
        + '<div class="cart-drawer__qty">'
        + '<button type="button" data-qty-step="-1" aria-label="' + T.dec + '">−</button>'
        + '<span class="cart-drawer__qty-value">' + item.quantity + '</span>'
        + '<button type="button" data-qty-step="1" aria-label="' + T.inc + '">+</button>'
        + '</div></div>'
        + '<div class="cart-drawer__item-side">'
        + '<span class="cart-drawer__item-price">' + money(item.final_line_price) + '</span>'
        + '<button type="button" class="cart-drawer__remove" data-remove>' + T.remove + '</button>'
        + '</div></div>';
    }).join('');
    subtotalEl.textContent = money(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price);
    foot.hidden = false;
    updateFreeShipping(cart);
    renderUpsell(cart);
  };

  const render = async () => {
    body.innerHTML = '<p class="cart-drawer__loading">' + T.loading + '</p>';
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      renderItems(cart);
    } catch (e) {
      body.innerHTML = '<p class="cart-drawer__empty">' + T.loadError + '</p>';
    }
  };

  const errorTimers = {};

  const showItemError = (key, msg) => {
    const item = body.querySelector('.cart-drawer__item[data-key="' + key + '"]');
    const main = item && item.querySelector('.cart-drawer__item-main');
    if (!main) return;
    let el = main.querySelector('.cart-drawer__item-error');
    if (!el) { el = document.createElement('p'); el.className = 'cart-drawer__item-error'; main.appendChild(el); }
    el.textContent = msg;
    clearTimeout(errorTimers[key]); // per-line timer: one line's error must not cancel another's cleanup
    errorTimers[key] = setTimeout(() => { if (el && el.remove) el.remove(); delete errorTimers[key]; }, 5000);
  };

  let changeQueue = Promise.resolve();
  // 数量変更は直列化する: 並行POSTは応答順が逆転すると古い状態で上書きしてしまう
  const change = (key, quantity) => {
    changeQueue = changeQueue.then(() => doChange(key, quantity)).catch(() => {});
    return changeQueue;
  };
  const doChange = async (key, quantity) => {
    const item = body.querySelector('.cart-drawer__item[data-key="' + key + '"]');
    const qtyEl = item && item.querySelector('.cart-drawer__qty-value');
    const prevQty = qtyEl ? qtyEl.textContent : '';
    if (qtyEl && quantity > 0) qtyEl.textContent = quantity;
    if (item) item.classList.add('is-updating');
    let errMsg = '', data = null, httpOk = false;
    try {
      const res = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      data = await res.json().catch(() => ({}));
      httpOk = res.ok;
      if (!httpOk) errMsg = data.description || data.message || T.changeFail;
    } catch (e) {
      errMsg = T.network;
    }
    if (data && data.items) {
      if (data.currency) cartCurrency = data.currency;
      const line = data.items.find((it) => it.key === key);
      if (!line) {
        renderItems(data);
      } else {
        if (httpOk && quantity > 0 && line.quantity < quantity) errMsg = errMsg || T.stockMax;
        if (qtyEl) qtyEl.textContent = line.quantity;
        const priceEl = item && item.querySelector('.cart-drawer__item-price');
        if (priceEl) priceEl.textContent = money(line.final_line_price);
        subtotalEl.textContent = money(data.items_subtotal_price != null ? data.items_subtotal_price : data.total_price);
        updateFreeShipping(data);
      }
      if (typeof refreshCartCount === 'function') refreshCartCount();
    } else if (qtyEl) {
      qtyEl.textContent = prevQty;
    }
    if (item) item.classList.remove('is-updating');
    if (errMsg) showItemError(key, errMsg);
  };

  document.querySelectorAll('.header__cart').forEach((link) => {
    link.addEventListener('click', (e) => { e.preventDefault(); open(); });
  });
  // Touch: swipe the panel to the right to close (vertical scroll wins)
  const panel = drawer.querySelector('.cart-drawer__panel');
  if (panel) {
    let sx = 0, sy = 0, swiping = false;
    panel.addEventListener('touchstart', (e) => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; swiping = true; }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
      if (!swiping) return;
      const t = e.touches[0];
      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      if (Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(dx)) { swiping = false; return; }
      if (dx > 70) { swiping = false; close(); }
    }, { passive: true });
  }
  drawer.querySelectorAll('[data-cart-close]').forEach((el) => el.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  body.addEventListener('click', (e) => {
    const item = e.target.closest('.cart-drawer__item');
    if (!item) return;
    const key = item.getAttribute('data-key');
    const qty = parseInt(item.querySelector('.cart-drawer__qty-value').textContent, 10) || 1;
    const step = e.target.closest('[data-qty-step]');
    if (step) { change(key, Math.max(0, qty + parseInt(step.getAttribute('data-qty-step'), 10))); }
    else if (e.target.closest('[data-remove]')) { change(key, 0); }
  });

  window.__openCartDrawer = open;
};


// --- Server-rendered product card fragment (sections/product-card-fragment.liquid) ---
// Returns a live, fully-styled card element (correct price/badges/i18n) or null.
const fetchCardFragment = async (handle) => {
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=product-card-fragment');
    if (!res.ok) return null;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return doc.querySelector('[data-card-fragment] .product-card');
  } catch (e) { return null; }
};

// --- Recently viewed: track PDP visits, render live cards on product pages ---
const initRecentlyViewed = () => {
  const KEY = 'lostFoundRecentlyViewed';
  const read = () => { try { return (Array.isArray(JSON.parse(localStorage.getItem(KEY))) ? JSON.parse(localStorage.getItem(KEY)) : []); } catch (e) { return []; } };
  const write = (l) => { try { localStorage.setItem(KEY, JSON.stringify(l)); } catch (e) {} };
  const m = location.pathname.match(/\/products\/([^/?#]+)/);
  const current = m ? decodeURIComponent(m[1]) : null;
  if (current && document.querySelector('.product-form')) {
    const list = read().filter((h) => h !== current);
    list.unshift(current);
    write(list.slice(0, 12));
  }
  const grid = document.querySelector('[data-recently-viewed]');
  if (!grid) return;
  const section = grid.closest('[data-recently-viewed-section]');
  const handles = read().filter((h) => h !== current).slice(0, 4);
  if (!handles.length) return;
  Promise.all(handles.map(fetchCardFragment)).then((cards) => {
    const ok = cards.filter(Boolean);
    if (!ok.length) return;
    ok.forEach((c) => grid.appendChild(c));
    if (section) section.hidden = false;
    fadeInImages(grid);
    initCardGalleries(grid);
  });
};

// --- Predictive search overlay (server-rendered via sections/predictive-search.liquid) ---
const initPredictiveSearch = () => {
  const overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;
  const input = overlay.querySelector('[data-search-input]');
  const results = overlay.querySelector('[data-search-results]');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let psCurrency = 'JPY'; // 買い手の表示通貨（/cart.jsが空カートでも返す）
  fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => { if (c && c.currency) psCurrency = c.currency; }).catch(() => {});
  const open = () => { overlay.hidden = false; document.body.classList.add('search-open'); setTimeout(() => { try { input.focus(); } catch (e) {} }, 30); };
  const close = () => { overlay.hidden = true; document.body.classList.remove('search-open'); };
  document.querySelectorAll('a.header__icon-link[href*="/search"]').forEach((a) => {
    a.addEventListener('click', (e) => { e.preventDefault(); open(); });
  });
  overlay.querySelectorAll('[data-search-close]').forEach((el) => el.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) close(); });
  // NOTE: this shop's ja locale returns 417 "Unsupported buyer locale" from
  // /search/suggest, so we render client-side from suggest.json and fall back
  // to the /en/ endpoint (data is locale-neutral; labels/urls localized here).
  const isJa = (document.documentElement.lang || 'ja').toLowerCase().indexOf('ja') === 0;
  const S = isJa
    ? { viewAll: 'すべての結果を見る', none: '見つかりませんでした', soldOut: '売り切れ' }
    : { viewAll: 'View all results', none: 'No results found', soldOut: 'Sold out' };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const localizeUrl = (u) => (isJa ? String(u).replace(/^\/en(\/|$)/, '/') : String(u));
  let suggestBase = null;
  const fetchSuggest = async (q, signal) => {
    const qs = 'search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
    const bases = suggestBase ? [suggestBase] : [root, '/en/'];
    for (const base of bases) {
      const res = await fetch(base + qs, { signal, headers: { Accept: 'application/json' } });
      if (res.ok) { suggestBase = base; return res.json(); }
      if (res.status !== 417) return null;
    }
    return null;
  };
  let timer;
  let ctrl;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { if (ctrl) ctrl.abort(); results.innerHTML = ''; return; }
    timer = setTimeout(async () => {
      try {
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        const data = await fetchSuggest(q, ctrl.signal);
        if (!data) { results.innerHTML = ''; return; }
        const prods = (((data.resources || {}).results || {}).products) || [];
        if (!prods.length) { results.innerHTML = '<p class="psearch__none">' + S.none + '</p>'; return; }
        results.innerHTML = '<ul class="psearch__list">' + prods.map((p) => {
          const iurl = p.featured_image && p.featured_image.url ? p.featured_image.url : '';
          const img = iurl ? iurl + (iurl.includes('?') ? '&' : '?') + 'width=120' : '';
          const rawPrice = parseFloat(String(p.price).replace(/,/g, '')) || 0;
          let price = S.soldOut;
          if (p.available) {
            if (psCurrency === 'JPY') price = '¥' + Math.round(rawPrice).toLocaleString('ja-JP');
            else { try { price = new Intl.NumberFormat(isJa ? 'ja-JP' : 'en-US', { style: 'currency', currency: psCurrency }).format(rawPrice); } catch (e) { price = rawPrice.toFixed(2) + ' ' + psCurrency; } }
          }
          return '<li><a class="psearch__item" href="' + esc(localizeUrl(p.url)) + '">'
            + (img ? '<img src="' + esc(img) + '" width="48" height="60" loading="lazy" alt="">' : '')
            + '<span class="psearch__main"><span class="psearch__title">' + esc(p.title) + '</span>'
            + '<span class="psearch__price">' + price + '</span></span></a></li>';
        }).join('') + '</ul>'
        + '<a class="psearch__all" href="' + root + 'search?q=' + encodeURIComponent(q) + '">' + S.viewAll + '</a>';
      } catch (e) { /* aborted or offline */ }
    }, 250);
  });
};

// --- Wishlist: localStorage-backed favourites (no account needed) ---
const initWishlist = () => {
  const KEY = 'lostFoundWishlist';
  const isJa = (document.documentElement.lang || 'ja').toLowerCase().indexOf('ja') === 0;
  const read = () => { try { return (Array.isArray(JSON.parse(localStorage.getItem(KEY))) ? JSON.parse(localStorage.getItem(KEY)) : []); } catch (e) { return []; } };
  const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {} };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const updateCount = () => {
    const n = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
  };
  const syncButtons = () => {
    const saved = new Set(read().map((i) => i.handle));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const on = saved.has(btn.getAttribute('data-handle'));
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = btn.querySelector('.wishlist-btn__label');
      if (label) label.textContent = on ? (btn.getAttribute('data-label-saved') || '') : (btn.getAttribute('data-label-add') || '');
    });
  };
  const renderPage = () => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    const list = read();
    const empty = document.querySelector('[data-wishlist-empty]');
    if (!list.length) { grid.innerHTML = ''; if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;
    const removeLabel = isJa ? 'お気に入りから削除' : 'Remove from wishlist';
    const heart = '<svg class="wishlist-btn__icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';
    grid.innerHTML = list.map((i) => {
      const media = i.image ? '<img src="' + esc(i.image) + '" alt="" loading="lazy">' : '<div class="placeholder"></div>';
      const price = i.price ? '<div class="price"><span>' + esc(i.price) + '</span></div>' : '';
      return '<article class="product-card" data-wl-handle="' + esc(i.handle) + '">'
        + '<a class="product-card__link" href="' + esc(i.url || '#') + '">'
        + '<div class="product-card__media">' + media + '</div>'
        + '<div class="product-card__content"><h3 class="product-card__title">' + esc(i.title) + '</h3>' + price + '</div></a>'
        + '<button class="wishlist-btn is-saved" type="button" data-wishlist-toggle data-handle="' + esc(i.handle) + '" aria-pressed="true" aria-label="' + removeLabel + '">'
        + heart + '</button></article>';
    }).join('');
    hydratePage();
  };
  // Replace the instant localStorage cards with server-rendered fragments so the
  // wishlist always shows CURRENT price / availability / badges (stored data is
  // frozen at heart-click time).
  const hydratePage = () => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    read().forEach(async (i) => {
      const card = await fetchCardFragment(i.handle);
      const target = grid.querySelector('[data-wl-handle="' + (window.CSS && CSS.escape ? CSS.escape(i.handle) : i.handle) + '"]');
      if (card && target) {
        target.replaceWith(card);
        syncButtons();
        fadeInImages(card);
        initCardGalleries(card);
      }
    });
  };
  const toggle = (btn) => {
    const handle = btn.getAttribute('data-handle');
    if (!handle) return;
    let list = read();
    if (list.some((i) => i.handle === handle)) {
      list = list.filter((i) => i.handle !== handle);
    } else {
      burstHearts(btn);
      list.unshift({
        handle: handle,
        title: btn.getAttribute('data-title') || '',
        url: btn.getAttribute('data-url') || '#',
        image: btn.getAttribute('data-image') || '',
        price: btn.getAttribute('data-price') || '',
      });
    }
    write(list);
    updateCount();
    syncButtons();
    renderPage();
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn);
  });

  updateCount();
  syncButtons();
  renderPage();
};


// --- Drop countdown: live timer to the next restock (target parsed as JST) ---
const initDropCountdown = () => {
  document.querySelectorAll('[data-countdown]').forEach((root) => {
    const raw = (root.getAttribute('data-target') || '').trim();
    if (!raw) return;
    let iso = raw.replace(/\//g, '-').replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) iso += ':00';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(iso)) iso += '+09:00';
    const target = new Date(iso);
    const section = root.closest('[data-drop]');
    if (isNaN(target.getTime())) { if (section) section.hidden = true; return; }
    const elD = root.querySelector('[data-cd-days]');
    const elH = root.querySelector('[data-cd-hours]');
    const elM = root.querySelector('[data-cd-mins]');
    const elS = root.querySelector('[data-cd-secs]');
    const live = section ? section.querySelector('[data-drop-live]') : null;
    const liveLabel = root.getAttribute('data-live-label') || '';
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    let timer;
    const tick = () => {
      if (document.hidden) return; // skip DOM writes in background tabs
      const diff = target.getTime() - Date.now();
      if (diff <= 0) {
        clearInterval(timer);
        if (liveLabel) {
          root.hidden = true;
          if (section) section.querySelectorAll('[data-drop-label]').forEach((el) => { el.hidden = true; });
          if (live) { live.textContent = liveLabel; live.hidden = false; }
        } else if (section) {
          section.hidden = true;
        }
        return;
      }
      const dd = Math.floor(diff / 86400000);
      if (elD) elD.textContent = dd;
      root.classList.toggle('cd-no-days', dd === 0);
      if (elH) elH.textContent = pad(Math.floor((diff % 86400000) / 3600000));
      if (elM) elM.textContent = pad(Math.floor((diff % 3600000) / 60000));
      if (elS) elS.textContent = pad(Math.floor((diff % 60000) / 1000));
    };
    tick();
    timer = setInterval(tick, 1000);
  });
};


// --- Announcement bar: rotate messages, dismissible (remembered per content) ---
const initAnnouncement = () => {
  document.querySelectorAll('[data-announcement]').forEach((bar) => {
    const items = bar.querySelectorAll('[data-announcement-item]');
    if (!items.length) return;
    // signature of current messages — a new promo re-shows the bar even if dismissed before
    const dropEl = bar.querySelector('[data-announcement-item][data-drop] [data-countdown]');
    const dropSig = dropEl ? 'drop:' + (dropEl.getAttribute('data-target') || '') + '|' : '';
    const sig = dropSig + Array.from(items).filter((el) => !el.hasAttribute('data-drop')).map((el) => el.textContent.trim()).join('|');
    let dismissed = false;
    try { dismissed = localStorage.getItem('lostFoundAnnDismissed') === sig; } catch (e) {}
    if (dismissed) { bar.hidden = true; return; }
    const closeBtn = bar.querySelector('[data-announcement-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => {
      bar.hidden = true;
      try { localStorage.setItem('lostFoundAnnDismissed', sig); } catch (e) {}
    });
    if (items.length > 1) {
      const interval = Math.max(2000, (parseFloat(bar.getAttribute('data-interval')) || 4) * 1000);
      const list = Array.from(items);
      let i = list.findIndex((el) => el.classList.contains('is-active'));
      if (i < 0) i = 0;
      // if the server-marked active item was hidden (e.g. invalid drop date), move to a visible one
      if (list[i].hidden) {
        list[i].classList.remove('is-active');
        const first = list.find((el) => !el.hidden);
        if (first) { first.classList.add('is-active'); i = list.indexOf(first); }
      }
      setInterval(() => {
        if (document.hidden || bar.hidden) return; // no rotation in background / after dismissal
        const visible = list.filter((el) => !el.hidden);
        if (visible.length < 2) return;
        list[i].classList.remove('is-active');
        let n = (i + 1) % list.length, guard = 0;
        while (list[n].hidden && guard++ < list.length) n = (n + 1) % list.length;
        i = n;
        list[i].classList.add('is-active');
      }, interval);
    }
  });
};


// --- Announcement CTA (#newsletter): let the native anchor scroll, then focus the email field ---
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href$="#newsletter"]');
  if (!link) return;
  const input = document.getElementById('FooterNewsletter');
  if (input) setTimeout(() => { try { input.focus({ preventScroll: true }); } catch (err) {} }, 700);
});
